import styled from "styled-components"

import { spacing } from "@minuthq/meatball-ui-react"

import { ItemsInShipment } from "src/components/Account/BillingPortal/UserOrders/UserOrderDetails/ItemsInShipment"
import { OrderAddress } from "src/components/Account/BillingPortal/UserOrders/UserOrderDetails/OrderAddress"
import { UserOrderInformation } from "src/components/Account/BillingPortal/UserOrders/UserOrderDetails/UserOrderInformation"
import { useFetchUserOrderDetails } from "src/data/billing/queries/billingQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { Divider } from "src/ui/Divider/Divider"
import { BreadcrumbView } from "src/ui/Layout/BreadcrumbView"
import { MBanner } from "src/ui/MBanner/MBanner"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { MText } from "src/ui/MText"

export function UserOrderDetails({ orderNumber }: { orderNumber: string }) {
  const { t, langKeys } = useTranslate()
  const { user_id } = useGetUser()

  const fetchUserOrderDetails = useFetchUserOrderDetails({
    userId: user_id,
    orderNumber,
  })

  if (fetchUserOrderDetails.isError) {
    return (
      <ContentWrapper orderNumber={orderNumber}>
        <MBanner type="error">
          {t(langKeys.failed_something_went_wrong)}
        </MBanner>
      </ContentWrapper>
    )
  }

  if (fetchUserOrderDetails.isLoading) {
    return (
      <ContentWrapper orderNumber={orderNumber}>
        <Loading />
      </ContentWrapper>
    )
  }

  const order = fetchUserOrderDetails.data

  return (
    <ContentWrapper orderNumber={orderNumber}>
      <Grid>
        <UserOrderInformation order={order} />

        <Divider />

        <ItemsInShipment order={order} />

        <Divider />

        <AddressGrid>
          <div>
            <MText variant="heading3" marginBottom={spacing.XS}>
              {t(langKeys.shipping_address)}
            </MText>
            <OrderAddress address={order.shipping_address} />
          </div>

          <div>
            <MText variant="heading3" marginBottom={spacing.XS}>
              {t(langKeys.billing_address)}
            </MText>
            <OrderAddress address={order.billing_address} />
          </div>
        </AddressGrid>
      </Grid>
    </ContentWrapper>
  )
}

function ContentWrapper({
  orderNumber,
  children,
}: {
  orderNumber: string
  children: React.ReactNode
}) {
  const { t, langKeys } = useTranslate()

  return (
    <BreadcrumbView
      title={<MText variant="heading1">#{orderNumber}</MText>}
      breadcrumbs={[
        {
          to: Routes.AccountOrders.location(),
          label: t(langKeys.ecommerce_order_plural),
        },
        {
          to: Routes.AccountOrderDetails.location(orderNumber),
          label: `#${orderNumber}`,
        },
      ]}
    >
      {children}
    </BreadcrumbView>
  )
}

const Grid = styled.div`
  display: grid;
  gap: ${spacing.L};
`

const AddressGrid = styled.div`
  display: flex;
  column-gap: ${spacing.XL5};
  row-gap: ${spacing.XL};
  flex-wrap: wrap;
`

function Loading() {
  return (
    <div>
      <MSkeleton width="10%" />
      <MSkeleton width="30%" />
      <MSkeleton width="20%" />
      <MSkeleton width="10%" />
      <MSkeleton width="40%" />
    </div>
  )
}
